/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import Select from "react-select";
import apiService from "services/apiService";
import GenericModal from "components/Modal";
import { colors } from "themes";
import { customSelectStyles } from "util/selectStyles";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";
// NOTE: shared resolver so a string `response.data` and an object
// `response.data` both end up as a readable toast message.
import { getApiErrorMessage } from "util/errorMessage";
import PhoneNumberInput from "components/PhoneNumberInput";
import {
  DEFAULT_COUNTRY_CODE,
  joinPhoneNumber,
  phoneNumberSchema,
  splitPhoneNumber,
} from "util/phoneCountry";
import { useAppContext } from "contexts/useAppContext";
import { parseJwtPayload, isAdminRole } from "util/moduleAccess";
import Loader from "components/Loader";
// Employee Type options are admin-configured (fetched at runtime).
import adminConfigService, { toSelectOptions } from "services/AdminConfig/adminConfigService";

// An employee is treated as "onboarding" when their account STATUS is "ONBOARDING".
// (It is a userStatus value — NOT a designation.) Match is case-insensitive.
const ONBOARDING = "onboarding";
const isOnboardingValue = (value?: string) =>
  typeof value === "string" && value.trim().toLowerCase() === ONBOARDING;

// UAN format: exactly 12 digits. `excludeEmptyString` is critical — without it
// this yup version fails `matches` on an empty string. UAN is optional for every
// employee, so the pattern only runs once a value is actually typed.
const UAN_REGEX = /^[0-9]{12}$/;
const UAN_FORMAT_MESSAGE = "UAN must be exactly 12 digits";
const uanBase = () =>
  yup
    .string()
    .trim()
    .matches(UAN_REGEX, { message: UAN_FORMAT_MESSAGE, excludeEmptyString: true });

// Shared `.when("userStatus", …)` config: relax a field to optional while the
// account status is "ONBOARDING", otherwise keep it required. This is reactive —
// switching the Status dropdown away from Onboarding re-imposes the normal rules,
// so completing onboarding requires the full set of details.
const optionalWhenOnboarding = (requiredMessage: string) => ({
  is: isOnboardingValue,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  then: (s: any) => s.optional(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  otherwise: (s: any) => s.required(requiredMessage),
});

const schema = yup.object().shape({
  // userStatus always has a value (ACTIVE / INACTIVE / ONBOARDING), so it stays
  // required. It must NOT gate on itself (that would be a cyclic dependency) — it
  // is instead the field every other rule below gates on.
  userStatus: yup.string().required("Status is required."),
  designation: yup
    .string()
    .when("userStatus", optionalWhenOnboarding("designation Is Required.")),
  gender: yup.string().when("userStatus", optionalWhenOnboarding("Gender is required.")),
  displayName: yup
    .string()
    .when("userStatus", optionalWhenOnboarding("Employee Name is Required.")),
  // DOJ default can be null (no joining date yet), so keep the base nullable.
  DOJ: yup
    .string()
    .nullable()
    .when("userStatus", optionalWhenOnboarding("Date Of Joining is required.")),
  // Email format still validated when present; only the required-ness is gated.
  email: validateNoTrailingOrLeadingWhitespace("Email")
    .email("Invalid email format")
    .when("userStatus", optionalWhenOnboarding("Email Is Required.")),
  // phoneNumberSchema without requiredMessage passes on empty; required is added
  // conditionally so the number format check still runs when a value is typed.
  mobileNumber: phoneNumberSchema("mobileCountryCode").when(
    "userStatus",
    optionalWhenOnboarding("Mobile Number Is Required ."),
  ),
  mobileCountryCode: yup.string().optional().default(DEFAULT_COUNTRY_CODE),
  role: yup.string().when("userStatus", optionalWhenOnboarding("Role is required.")),
  // --- UAN VALIDATION --- Optional for every employee; the 12-digit format is
  // only enforced once a value is actually typed (excludeEmptyString on the regex).
  UAN: uanBase().optional(),
  // Admin-configured employment type (Full-time, Contract, Intern, …). Optional so
  // existing employees without a type set can still be saved.
  employmentType: yup.string().optional(),
});

interface AddProps {
  onClose: () => void;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // The optional second arg carries the updated employee so a caller (e.g. the
  // details screen) can refresh its view without an extra fetch. Callers that
  // only need the message (e.g. the list) can ignore it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess: (message: string, updatedEmployee?: any) => void;
  onError: (message: string) => void;
}
interface GenderOption {
  value: string;
  label: string;
}

interface StoreResponseProp {
  projectNames: string[];
  assetModels: string[];
}
interface userOptions {
  value: number;
  label: string;
}

export const userOptions: userOptions[] = [
  { value: 1, label: "User" },
  { value: 2, label: "Admin" },
  { value: 3, label: "Sys_Admin" },
  { value: 4, label: "Manager" },
];

export default function EditEmployee({ onClose, data, onSuccess, onError }: AddProps) {
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      ...data,
      mobileNumber: splitPhoneNumber(data.mobileNumber).nationalNumber,
      mobileCountryCode: splitPhoneNumber(data.mobileNumber).countryCode,
      DOJ: data.DOJ ? new Date(data.DOJ).toISOString().split("T")[0] : null,
      // Initialize UAN (checks profileDetails first, then falls back to root level)
      UAN: data?.profileDetails?.UAN || data?.UAN || "",
      // The API returns the value as `empType`; the form field is `employmentType`.
      employmentType: data?.employmentType ?? data?.empType ?? "",
    },
  });
  const [isUpdate, setIsUpdate] = useState(false);
  // True while the deactivation PREVIEW request is in flight, so the popup can
  // show a loader instead of prematurely rendering the "no assets" message.
  const [isDeactivationLoading, setIsDeactivationLoading] = useState(false);
  const {
    formState: { isDirty, isSubmitting },
  } = methods;

  // Check if the current employee being edited is Inactive
  const isInactiveEmployee = data.userStatus === "INACTIVE";

  // Hide the Status and Role fields when an admin is editing their OWN record,
  // so an admin cannot deactivate their own account or change their own role.
  // The values still travel in the payload (they stay in the form defaults),
  // they just can't be edited here.
  const { user, email: loggedInEmail, userRole } = useAppContext();
  const storedUserEmail = (() => {
    try {
      const stored = typeof window !== "undefined" ? localStorage.getItem("user") : null;
      return stored ? JSON.parse(stored)?.email || "" : "";
    } catch {
      return "";
    }
  })();
  const currentUserEmail = (loggedInEmail || user?.email || storedUserEmail || "")
    .toString()
    .toLowerCase();
  const jwtPayload = parseJwtPayload(
    typeof window !== "undefined" ? localStorage.getItem("accessToken") : null
  );
  const currentUserIsAdmin = isAdminRole(jwtPayload, userRole ?? user?.role);
  const isEditingSelf =
    !!currentUserEmail && String(data?.email || "").toLowerCase() === currentUserEmail;
  const hideStatusAndRole = currentUserIsAdmin && isEditingSelf;

  // Deactivation-preview response: either { projectNames, assetModels } (Case 1)
  // or the "No projects or assets found for the user." string (Case 2).
  const [storeResponse, setStoreResponse] = useState<StoreResponseProp | string | null>(null);

  // Employee Type options from Admin Config. The saved value is guaranteed to be
  // selectable even if it was later renamed/removed from the config.
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  // Designation options from Admin Config (rendered purely from the server).
  const [designationOptions, setDesignationOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    adminConfigService
      .getEmployeeTypes()
      .then(({ data }) => setEmploymentTypeOptions(toSelectOptions(data)))
      .catch(() => setEmploymentTypeOptions([]));

    adminConfigService
      .getDesignations()
      .then(({ data }) => setDesignationOptions(toSelectOptions(data)))
      .catch(() => setDesignationOptions([]));
  }, []);

  // Current employment type (watched so the controlled Select reflects edits and
  // the value loaded from `data`), with a fallback option if it's not in the list.
  const selectedEmploymentType =
    methods.watch("employmentType") ?? data.employmentType ?? data.empType ?? "";
  const employmentTypeValue =
    employmentTypeOptions.find((o) => o.value === selectedEmploymentType) ??
    (selectedEmploymentType
      ? { value: selectedEmploymentType, label: selectedEmploymentType }
      : null);

  // Current designation, with a fallback option so a saved value stays selectable
  // even if it was later renamed/removed from the config or options are still loading.
  const selectedDesignation = methods.watch("designation") ?? data.designation ?? "";
  const designationValue =
    designationOptions.find((o) => o.value === selectedDesignation) ??
    (selectedDesignation ? { value: selectedDesignation, label: selectedDesignation } : null);

  const buildUserPayload = (values: any) => {
    const { mobileCountryCode, employmentType, ...userValues } = values || {};
    return {
      ...userValues,
      mobileNumber: joinPhoneNumber(mobileCountryCode, values?.mobileNumber),
      // The form field is `employmentType`, but the API expects `empType`.
      empType: employmentType,
    };
  };

  // Drive the required markers below: hidden while the account status is
  // "ONBOARDING" (reactively watched), matching the conditional schema above.
  const selectedUserStatus = methods.watch("userStatus") ?? data.userStatus;
  const isOnboardingSelected = isOnboardingValue(selectedUserStatus);

  // Parse the deactivation-preview response: an object with lists → Case 1; the
  // "No projects or assets found…" string (or empty lists) → Case 2.
  const deactivationInfo =
    storeResponse && typeof storeResponse === "object" ? storeResponse : null;
  const deactivationAssets = deactivationInfo?.assetModels ?? [];
  const deactivationProjects = deactivationInfo?.projectNames ?? [];
  const hasAssetsOrProjects =
    deactivationAssets.length > 0 || deactivationProjects.length > 0;
  const deactivationName = data?.displayName || "this employee";

  /**
   * The exact payload the normal employee update sent, kept so the follow-up
   * status change can reuse it.
   * NOTE: react-hook-form only calls onSubmit once the schema passes, so this
   * always holds a complete, validated set of employee details.
   */
  const lastSubmittedValuesRef = useRef<any>(null);

  /**
   * NOTE: the "Change User Status" confirmation used to be opened from the
   * Submit button's onClick, which fires on every click regardless of whether
   * the form is valid. Opening it here instead keeps required-field validation
   * in force — the dialog can only appear after the schema has passed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit: SubmitHandler<any> = (formData: any) => {
    const normalizedValues = buildUserPayload(formData);
    lastSubmittedValuesRef.current = normalizedValues;

    // The "Change User Status" asset-collection confirmation is ONLY for
    // ACTIVE → INACTIVE (deactivation reclaims assets and removes the user from
    // projects). Any other transition (INACTIVE → ACTIVE, ONBOARDING → ACTIVE,
    // etc.) is applied by the normal update below with no extra pop-up.
    const isDeactivating =
      String(data.userStatus || "").toUpperCase() === "ACTIVE" &&
      String(formData.userStatus || "").toUpperCase() === "INACTIVE";

    if (isDeactivating) {
      // Open the popup in a loading state and clear any stale preview data so the
      // "no assets" message can't flash before the response arrives.
      setStoreResponse(null);
      setIsDeactivationLoading(true);
      setIsUpdate(true);
    }

    // Ensure UAN is explicitly structured if backend expects it inside profileDetails
    const payload = {
      ...normalizedValues,
      profileDetails: {
        ...(data.profileDetails || {}),
        UAN: formData.UAN,
      },
    };

    apiService
      .put("api/user", payload)
      .then((res) => {
        if (res.status === 200) setStoreResponse(res.data);
        // For a deactivation this PUT is only a PREVIEW — keep the modal open for
        // the asset-collection confirmation and show NO toast yet (nothing is
        // finalised until the user confirms). For any other update, reflect it and
        // notify immediately.
        if (!isDeactivating) {
          onSuccess("Employee Details Updated successfully", {
            ...data,
            ...payload,
            employmentType: formData.employmentType,
          });
        }
      })
      .catch((err) => {
        console.log("err :>> ", err);
        // CHANGE: was `err?.response?.data || ...`, which handed the RAW object
        // to `onError(message: string)` whenever the API replied with JSON —
        // React then threw "Objects are not valid as a React child". The helper
        // type-checks the payload (string → use it, object → read its
        // parameters) before anything reaches the toast.
        onError(getApiErrorMessage(err, "Something went wrong"));
        // On a failed preview, close the popup rather than showing a misleading state.
        if (isDeactivating) setIsUpdate(false);
      })
      .finally(() => {
        // Keep the modal open only for the deactivation asset-collection step;
        // otherwise close immediately.
        if (!isDeactivating) onClose();
        else setIsDeactivationLoading(false);
      });
  };

  /**
   * NOTE: this used to build the payload as `{ ...data, userStatus }`, where
   * `data` is the employee row handed over by the list. That row does not carry
   * the full employee record (it is what leaves designation / DOJ / gender /
   * mobileNumber blank when the modal opens), and it also discards anything the
   * user just typed — so switching a user to Inactive posted an incomplete body
   * and the API rejected it for missing required details.
   *
   * The payload is now the same validated object the normal update sent, with
   * only userStatus swapped, so both requests carry identical employee fields.
   * The form values are used if a submitted payload is somehow unavailable, and
   * the row stays as the last-resort base so no unrelated key is dropped.
   */
  const userStatusteHandle = () => {
    const updatedStatus = methods.getValues("userStatus");
    const submittedValues =
      lastSubmittedValuesRef.current || buildUserPayload(methods.getValues());
    // Drop the original `employmentType` key from the spread so only `empType`
    // (from buildUserPayload) goes to the API.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { employmentType: _omitEmploymentType, ...restData } = data || {};
    const updatedUser = { ...restData, ...submittedValues, userStatus: updatedStatus };
    apiService
      .put("api/user?shouldRemoveUser=true", updatedUser)
      .then((res) => {
        if (res.status === 200) {
          // UI reads `employmentType`; the API body uses `empType`.
          onSuccess("User status updated successfully", {
            ...updatedUser,
            employmentType: submittedValues.empType,
          });
        } else {
          onError("Something went wrong while updating user status");
        }
      })
      .catch((error) => {
        console.error(error);
        // CHANGE: same resolver as the update above — this path only read
        // `data.message`, so a plain-string error body fell through to the
        // generic "Something went wrong" and hid the real reason.
        onError(getApiErrorMessage(error, "Something went wrong"));
      })
      .finally(() => {
        onClose();
      });
  };

  const genderOptions: GenderOption[] = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];
  const statusOptions: GenderOption[] = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    // Onboarding is a user-status value; while it is selected the form is optional.
    { value: "ONBOARDING", label: "Onboarding" },
  ];


  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {/* 3-column grid keeps the whole form within the modal (no scroll). */}
          <div className="grid gap-x-4 grid-cols-2 md:grid-cols-3">
            <Input
              name="displayName"
              label="Employee Name"
              placeholder={`Enter Full Name`}
              required={!isOnboardingSelected}
              disabled
            />
            <Input
              name="email"
              label="Email Address"
              placeholder={`Enter Email Address`}
              required={!isOnboardingSelected}
              disabled
            />
            <Input
              name="DOJ"
              label="Date Of Joining"
              type="date"
              required={!isOnboardingSelected}
              disabled={data.DOJ ? true : false}
            />
            <PhoneNumberInput
              name="mobileNumber"
              countryCodeName="mobileCountryCode"
              label="Mobile Number"
              placeholder={`Enter Mobile Number`}
              required={!isOnboardingSelected}
              // Disable if user is inactive AND the initial mobile number is not empty
              disabled={isInactiveEmployee && !!data.mobileNumber}
            />
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Select Gender{" "}
                {!isOnboardingSelected && <span className="text-red-500">*</span>}
              </label>
              <Select
                {...methods.register("gender", { required: "Gender is required." })}
                options={genderOptions}
                placeholder="Select Gender"
                className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                // Disable if user is inactive AND gender is not empty
                isDisabled={isInactiveEmployee && !!data.gender}
                onChange={(selectedOption) => {
                  selectedOption &&
                    methods.setValue("gender", selectedOption.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                }}
                defaultValue={genderOptions.find(
                  (option) => option.value === data.gender
                )}
              />
              <div className="h-6 mt-1">
                {methods.formState.errors.gender?.message ? (
                  <p className="text-red-500 text-xs font-medium leading-tight">
                    {methods.formState.errors.gender.message as ReactNode}
                  </p>
                ) : (
                  <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                )}
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Designation{" "}
                {!isOnboardingSelected && <span className="text-red-500">*</span>}
              </label>
              <Select
                {...methods.register("designation", {
                  required: "Designation is required.",
                })}
                options={designationOptions}
                placeholder="Select Designation"
                className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                // Disable if user is inactive AND designation is not empty
                isDisabled={isInactiveEmployee && !!data.designation}
                onChange={(selectedOption) => {
                  selectedOption &&
                    methods.setValue("designation", (selectedOption as { value: string }).value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                }}
                value={designationValue}
              />
              <div className="h-6 mt-1">
                {methods.formState.errors.designation?.message ? (
                  <p className="text-red-500 text-xs font-medium leading-tight">
                    {methods.formState.errors.designation.message as ReactNode}
                  </p>
                ) : (
                  <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                )}
              </div>
            </div>

            {/* Employee Type — options rendered dynamically from Admin Config */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Employee Type
              </label>
              <Select
                {...methods.register("employmentType")}
                options={employmentTypeOptions}
                placeholder="Select Employee Type"
                className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                isClearable
                // Disable if user is inactive AND an employment type is already set
                isDisabled={isInactiveEmployee && !!(data.employmentType || data.empType)}
                onChange={(selectedOption) => {
                  methods.setValue(
                    "employmentType",
                    (selectedOption as { value: string } | null)?.value ?? "",
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
                value={employmentTypeValue}
              />
              <div className="h-6 mt-1">
                {methods.formState.errors.employmentType?.message ? (
                  <p className="text-red-500 text-xs font-medium leading-tight">
                    {methods.formState.errors.employmentType.message as ReactNode}
                  </p>
                ) : (
                  <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                )}
              </div>
            </div>

            {!hideStatusAndRole && (
              <>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Select Status <span className="text-red-500">*</span>
                  </label>
                  <Select
                    {...methods.register("userStatus", {
                      required: "Status is required.",
                    })}
                    options={statusOptions}
                    placeholder="Select Status"
                    className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    // Status is NEVER disabled because we need to allow reactivation
                    onChange={(selectedOption) => {
                      if (!selectedOption) return;
                      methods.setValue("userStatus", selectedOption.value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      // Status drives the optional/required gate — re-validate every
                      // field so errors clear (or re-appear) as it flips.
                      methods.trigger();
                    }}
                    defaultValue={statusOptions.find(
                      (option) => option.value === data.userStatus
                    )}
                  />
                  <div className="h-6 mt-1">
                    {methods.formState.errors.userStatus?.message ? (
                      <p className="text-red-500 text-xs font-medium leading-tight">
                        {methods.formState.errors.userStatus.message as ReactNode}
                      </p>
                    ) : (
                      <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                    )}
                  </div>
                </div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Select Role{" "}
                    {!isOnboardingSelected && <span className="text-red-500">*</span>}
                  </label>
                  <Select
                    {...methods.register("role", { required: "Role is required." })}
                    options={userOptions}
                    placeholder="Select Role"
                    className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-800 transition-all shadow-sm"
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    // Disable if user is inactive AND role is not empty
                    isDisabled={isInactiveEmployee && !!data.role}
                    onChange={(selectedOption) => {
                      selectedOption &&
                        methods.setValue("role", selectedOption.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                    }}
                    defaultValue={userOptions.find(
                      (option) => option.value === data.role
                    )}
                  />
                  <div className="h-6 mt-1">
                    {methods.formState.errors.role?.message ? (
                      <p className="text-red-500 text-xs font-medium leading-tight">
                        {methods.formState.errors.role.message as ReactNode}
                      </p>
                    ) : (
                      <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                    )}
                  </div>
                </div>
              </>
            )}

            <Input
              name="UAN"
              label="UAN Number"
              placeholder={`Eg : 100123456789`}
              type="number"
              // UAN is optional for every employee — no required marker.
              // Disable if user is inactive AND UAN is not empty
              disabled={isInactiveEmployee && !!(data?.profileDetails?.UAN || data?.UAN)}
            />
          </div>

          <div className="flex justify-center gap-3 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-2.5 text-sm font-bold text-slate-600  bg-white  hover:bg-slate-100  focus:ring-4 focus:outline-none focus:ring-slate-200 dark:focus:ring-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 transition-all shadow-sm active:scale-95"
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ backgroundColor: colors.primary }}
              className={`text-white px-8 py-2.5 rounded-xl font-bold transition-all shadow-lg ${!isDirty || isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:opacity-90 active:scale-95"
                }`}
              disabled={!isDirty || isSubmitting}
            >
              Submit
            </button>
          </div>
        </form>
      </FormProvider>
      {isUpdate && (
        <GenericModal
          isOpen={isUpdate}
          onClose={() => setIsUpdate(false)}
          header={hasAssetsOrProjects ? "Confirm Deactivation" : "Deactivate Employee"}
        >
          <div className="text-slate-900 dark:text-white">
            {isDeactivationLoading ? (
              // ── Loading: the deactivation preview is still in flight ──
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Loader />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Checking assigned assets and projects…
                </p>
              </div>
            ) : hasAssetsOrProjects ? (
              // ── Case 1: the employee has assets and/or projects to hand over ──
              <>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                  Before deactivating{" "}
                  <span className="font-semibold">{deactivationName}</span>, please
                  collect the assigned assets. The employee will also be removed
                  from the projects listed below.
                </p>

                {deactivationAssets.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                      Assets to collect ({deactivationAssets.length})
                    </h4>
                    <ul className="flex flex-wrap gap-2">
                      {deactivationAssets.map((asset, i) => (
                        <li
                          key={`asset-${asset}-${i}`}
                          className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20 text-sm font-medium"
                        >
                          {asset}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {deactivationProjects.length > 0 && (
                  <div className="mb-1">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                      Removed from projects ({deactivationProjects.length})
                    </h4>
                    <ul className="flex flex-wrap gap-2">
                      {deactivationProjects.map((project, i) => (
                        <li
                          key={`project-${project}-${i}`}
                          className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-sm font-medium"
                        >
                          {project}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              // ── Case 2: nothing assigned — just confirm deactivation ──
              <div className="text-center py-2">
                <svg
                  className="mx-auto mb-3 text-gray-400 dark:text-gray-300 w-12 h-12"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                <p className="text-base font-medium text-slate-700 dark:text-slate-200 mb-1">
                  No projects or assets found for the user.
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Are you sure you want to mark{" "}
                  <span className="font-semibold">{deactivationName}</span> as
                  inactive?
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="py-2.5 px-5 text-sm font-medium text-slate-900 focus:outline-none bg-white rounded-xl border border-slate-200 hover:bg-slate-50 hover:text-primary focus:z-10 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600 dark:hover:text-white dark:hover:bg-slate-700 transition-all active:scale-95"
                onClick={() => setIsUpdate(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeactivationLoading}
                style={{ backgroundColor: colors.error }}
                className="text-white hover:opacity-90 focus:ring-4 focus:outline-none shadow-lg font-medium rounded-xl text-sm inline-flex items-center px-5 py-2.5 text-center transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => userStatusteHandle()}
              >
                {hasAssetsOrProjects ? "Confirm & Deactivate" : "Yes, Deactivate"}
              </button>
            </div>
          </div>
        </GenericModal>
      )}
    </>
  );
}